import sjcl, { type SjclCipherEncrypted } from 'sjcl';

/**
 * @function doEncryptLocalStorageSetItem
 * @desc encrypts the value and sets to property(name) provided to localStorage.
 *
 * @param {String} name - property name for setItem of localStorage.
 * @param {String} secret - secret used for encrypt.
 * @param {String} value -  value set into the property of localStorage.
 * @returns void
 */
const doEncryptLocalStorageSetItem = (
  name: string,
  value: string,
  secret: string
): void => {
  const encryptedValue: SjclCipherEncrypted = sjcl.encrypt(secret, value);
  // Note: Using JSON.stringify is causing an issue:
  // encrypted data with doEncryptLocalStorageSetItem can not be decrypted with doDecrypt unless it is parsed.
  // This is a very tricky situation that I think is due to wrong type definition of the encrypt method in the sjcl package;
  // SjclCipherEncrypted is supposed to be an object. However it is actually a string when it is returned in javaScript.
  // Going around this error with JSON.parse and JSON.stringify is causing an error in our client applications because not all data that
  // we encrypt is a JSON. I believe the best thing to do now, until all our repositories are converted to Typescript is to ignore this error.
  // This ensures backward compatibility between our raven-tools (TypeScript and JavaScript).

  // @ts-expect-error blah
  localStorage.setItem(name, encryptedValue);
};

/**
 * @function doDecryptLocalStorageGetItem
 * @desc decrypts the localStorage getItem and returns the value.
 *
 * @param {String} name - property name to retrieve the value from localStorage.
 * @param {String} secret - secret used for decrypt.
 * @returns {String|Boolean} - boolean false on fail, string on success
 */
const doDecryptLocalStorageGetItem = (
  name: string,
  secret: string
): string | boolean => {
  const localStorageValue = localStorage.getItem(name);
  if (localStorageValue != null) {
    const decryptedValue = sjcl.decrypt(secret, localStorageValue);
    return decryptedValue;
  }
  return false;
};

/**
 * Wrapper for scjs.decrypt which itself is a convenience method for decrypting json formatted data
 * @param {String} password The password or key used to encrypt the data.
 * @param {String} value must be an JSON string with the minimum following properties...
 * @param {String} value.ct The ciphertext to decrypt.
 * @param {String} value.iv An initialization vector (IV) is an arbitrary number that can be used along with a secret key for data encryption.
 * @param {String} value.salt adds an additional value to the end of the password that changes the hash value produced when encrypting
 * @return {String} The plaintext.
 * @throws {sjcl.exception.invalid} if a parameter is invalid.
 * @throws {sjcl.exception.corrupt} if the ciphertext is corrupt. */
const doDecrypt = (password: string, value: string): string => {
  return sjcl.decrypt(password, value);
};

/**
 * Wrapper for sjcl.encrypt
 * @param {String} password The password or key.
 * @param {String} plaintext The data to encrypt.
 * @return {SjclCipherEncrypted} JSON containing ciphertext, and complete parameters used to encrypt the data, including salt and iv.
 */
const doEncrypt = (
  password: string,
  plaintext: string
): SjclCipherEncrypted | string => {
  return sjcl.encrypt(password, plaintext);
};

const doEncryptLocal = {
  doDecrypt,
  doDecryptLocalStorageGetItem,
  doEncrypt,
  doEncryptLocalStorageSetItem,
};

export default doEncryptLocal;
